import { BusinessObjectBase } from './base';
import { processDesignActivityCountSchema } from '@schemas/business-objects/process-design-activity-count';
import type { ProcessDesign } from './process-design';
import type { Tenant } from './tenant';
import type { Id, TokenStatus, Uuid } from '../base-types';

export interface ProcessDesignActivityCountData {
  readonly id: string;
  readonly activityId?: string;
  readonly status?: TokenStatus;
  readonly tokenCount?: number;
  readonly designUuid?: Uuid;
  readonly processDesignId: Id;
  readonly processDesign?: ProcessDesign;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface ProcessDesignActivityCount extends Readonly<ProcessDesignActivityCountData> {}
type ProcessDesignActivityCountCreateWithChildrenData =
  Partial<ProcessDesignActivityCountData>;

export class ProcessDesignActivityCount extends BusinessObjectBase {
  static readonly entityName = 'ProcessDesignActivityCount' as const;
  static readonly plural = 'process-design-activity-counts';
  static schema = processDesignActivityCountSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessDesignActivityCount[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessDesignActivityCount(data));
  }

  static async findOne(
    filter?: any,
  ): Promise<ProcessDesignActivityCount | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessDesignActivityCount(data) : null;
  }

  static async findById(
    id: string,
    filter?: any,
  ): Promise<ProcessDesignActivityCount> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessDesignActivityCount(data);
  }

  static async create(
    data: Omit<ProcessDesignActivityCountData, 'id'>,
  ): Promise<ProcessDesignActivityCount> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessDesignActivityCount(result);
  }

  static async createMany(
    data: Omit<ProcessDesignActivityCountData, 'id'>[],
  ): Promise<ProcessDesignActivityCount[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesignActivityCount(item));
  }

  static async createWithChildren(
    data: ProcessDesignActivityCountCreateWithChildrenData,
  ): Promise<ProcessDesignActivityCount> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessDesignActivityCount(result);
  }

  static async createManyWithChildren(
    data: ProcessDesignActivityCountCreateWithChildrenData[],
  ): Promise<ProcessDesignActivityCount[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessDesignActivityCount(item));
  }

  static async updateById(
    id: string,
    updates: Partial<ProcessDesignActivityCountData>,
  ): Promise<ProcessDesignActivityCount> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessDesignActivityCount(result);
  }

  static async deleteById(id: string): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
